import Chance from 'chance';

import Tracer from './tracer';
import { localStorage } from '../env/browser';

const chance = new Chance();

const ACTIVE = '1';
const INACTIVE = '0';

interface FeatureToggleCache {
  activeRolloutPercentage?: number | string;
  [key: string]: unknown;
}

function storageKey(featureName: string): string {
  return `feature_toggle_${featureName}`;
}

function cachedValue(featureName: string): FeatureToggleCache {
  const cache = localStorage.getItem(storageKey(featureName));

  if (!cache) {
    return {};
  }

  return JSON.parse(cache) as FeatureToggleCache;
}

function isCacheEmpty(cache: FeatureToggleCache): boolean {
  return Object.keys(cache).length === 0;
}

const castToggleValue = (toggleStrValue: string): boolean =>
  Boolean(Number(toggleStrValue));

function cacheFeatureToggle(
  featureName: string,
  percentage: number,
  isActive: boolean,
  existingCache: FeatureToggleCache
): void {
  const percentageStr = String(percentage);

  localStorage.setItem(
    storageKey(featureName),
    JSON.stringify({
      ...existingCache,
      [percentageStr]: isActive ? ACTIVE : INACTIVE,
      activeRolloutPercentage: percentage,
    })
  );
}

function setTracerRolloutInfo(
  featureName: string,
  isActive: boolean,
  percentage: number
): void {
  const featureRolloutContext =
    Tracer.getGlobalContextProperty('feature_rollout');

  Tracer.setGlobalContext('feature_rollout', {
    ...(featureRolloutContext || {}),
    [featureName]: {
      percentage,
      value: isActive,
    },
  });
}

function toggleValue(
  cache: FeatureToggleCache,
  newRolloutPercentage: number
): boolean {
  const freshToggleValue = chance.bool({ likelihood: newRolloutPercentage });

  if (isCacheEmpty(cache)) {
    return freshToggleValue;
  }

  if (newRolloutPercentage > Number(cache.activeRolloutPercentage)) {
    const currentToggleValue = castToggleValue(
      String(cache[String(cache.activeRolloutPercentage)])
    );

    return currentToggleValue === true ? true : freshToggleValue;
  }

  return freshToggleValue;
}

const FeatureRollout = {
  features(): Set<string> {
    return new Set(['services-app-refactor', 'api-consumption-analysis']);
  },

  validateFeatureName(featureName: string): void {
    if (!this.features().has(featureName)) {
      throw new Error(`Unknown feature toggle name: ${featureName}`);
    }
  },

  activate(
    featureName: string,
    { percentage }: { percentage: number }
  ): boolean {
    this.validateFeatureName(featureName);

    const currentValue = cachedValue(featureName);
    const percentageStr = String(percentage);

    if (currentValue[percentageStr] === undefined) {
      const shouldToggle = toggleValue(currentValue, percentage);

      cacheFeatureToggle(featureName, percentage, shouldToggle, currentValue);

      setTracerRolloutInfo(featureName, shouldToggle, percentage);

      return shouldToggle;
    }

    const currentToggleValue = castToggleValue(
      String(currentValue[percentageStr])
    );

    if (percentageStr !== String(currentValue.activeRolloutPercentage)) {
      localStorage.setItem(
        storageKey(featureName),
        JSON.stringify({
          ...currentValue,
          activeRolloutPercentage: percentage,
        })
      );
    }

    setTracerRolloutInfo(featureName, currentToggleValue, percentage);

    return currentToggleValue;
  },

  isActive(featureName: string): boolean {
    this.validateFeatureName(featureName);

    const cache = cachedValue(featureName);
    const { activeRolloutPercentage } = cache;

    return !!(
      cache[String(activeRolloutPercentage)] &&
      castToggleValue(String(cache[String(activeRolloutPercentage)]))
    );
  },
};

export default FeatureRollout;
